import { useEffect } from 'react'
import styled from '@emotion/styled'
import { useTheme } from '@emotion/react'
import { RevenueCenter } from '@open-tender/types'
import { closeModal, useAppDispatch } from '@open-tender/cloud'
import {
  Body,
  Button,
  Heading,
  ModalContent,
  ModalView,
  Preface,
} from 'components'
import { ChevronRight } from 'components/icons'
import {
  sendModalConfirm,
  sendModalDismissed,
  sendModalImpression,
} from 'app/analytics'

const tintWithWhite = (hex: string, ratio: number): string => {
  const value = hex.replace('#', '')
  const bigint = parseInt(value, 16)
  const r = (bigint >> 16) & 255
  const g = (bigint >> 8) & 255
  const b = bigint & 255
  const mix = (channel: number) => Math.round(channel * ratio + 255 * (1 - ratio))
  return `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`
}

const ConfirmStorePin = ({
  size,
  color,
  holeColor,
}: {
  size: number
  color: string
  holeColor: string
}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path
      d="M20 10C20 14.4183 12 22 12 22C12 22 4 14.4183 4 10C4 5.58172 7.58172 2 12 2C16.4183 2 20 5.58172 20 10Z"
      fill={color}
    />
    <circle cx="12" cy="9.5" r="2.75" fill={holeColor} />
  </svg>
)

const ConfirmStoreView = styled.div`
  text-align: center;
`

const ConfirmStoreIcon = styled.div`
  width: 4.6rem;
  height: 4.6rem;
  margin: 0 auto 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: ${(props) => props.theme.colors.tertiary};
  background-color: ${(props) => tintWithWhite(props.theme.colors.tertiary, 0.12)};
`

const ConfirmStoreName = styled(Heading)`
  display: block;
  font-size: ${(props) => props.theme.fonts.sizes.xxBig};
  text-transform: uppercase;

  && {
    margin: 0.4rem 0 1rem;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    font-size: ${(props) => props.theme.fonts.sizes.xBig};
  }
`

const ConfirmStoreAddress = styled(Body)`
  display: block;
  font-size: ${(props) => props.theme.fonts.sizes.small};

  && {
    margin: 0;
  }
`

const ConfirmStoreButtons = styled.div`
  margin: 1.8rem 0 0;

  button {
    display: block;
    width: 100%;
    margin: 0 0 1rem;

    &:last-of-type {
      margin: 0;
    }
  }
`

const ConfirmStoreSecondaryButton = styled(Button)`
  &,
  &:hover,
  &:disabled {
    background-color: ${(props) => props.theme.bgColors.light};
    border-color: ${(props) => props.theme.colors.dark};
    color: ${(props) => props.theme.colors.dark};
  }
`

const ConfirmStoreChevron = styled.span`
  display: block;
  flex-shrink: 0;
  line-height: 0;
  margin: 0 0 0 0.8rem;
`

const ConfirmStoreFootnote = styled(Body)`
  display: block;
  max-width: 26rem;
  font-size: ${(props) => props.theme.fonts.sizes.xSmall};
  color: ${(props) => props.theme.colors.tertiary};

  && {
    margin: 1.2rem auto 0;
  }
`

export interface ConfirmStoreModalProps extends Record<string, unknown> {
  revenueCenter: RevenueCenter
  onConfirm: () => void
}

const ConfirmStore = ({ revenueCenter, onConfirm }: ConfirmStoreModalProps) => {
  const dispatch = useAppDispatch()
  const theme = useTheme()
  const { name, address, revenue_center_id } = revenueCenter
  const { street, city, state, postal_code: postalCode } = address || {}
  const pinColor = theme.colors.tertiary
  const pinHoleColor = tintWithWhite(pinColor, 0.12)

  useEffect(() => {
    sendModalImpression({ name, revenue_center_id })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const confirm = () => {
    sendModalConfirm({ name, revenue_center_id })
    // Close here, not in the caller: the asap path only navigates and would leave this open.
    dispatch(closeModal())
    onConfirm()
  }

  const dismiss = () => {
    sendModalDismissed({ name, revenue_center_id })
    dispatch(closeModal())
  }

  return (
    <ModalView>
      <ModalContent close={false}>
        <ConfirmStoreView>
          <ConfirmStoreIcon>
            <ConfirmStorePin size={30} color={pinColor} holeColor={pinHoleColor} />
          </ConfirmStoreIcon>
          <Preface size="small" color="tertiary">
            Confirm your store
          </Preface>
          <ConfirmStoreName as="p">{name}</ConfirmStoreName>
          {street && <ConfirmStoreAddress as="p">{street}</ConfirmStoreAddress>}
          <ConfirmStoreAddress as="p">
            {city}, {state} {postalCode}
          </ConfirmStoreAddress>
          <ConfirmStoreButtons>
            {/* Chevron trails the label, so it's a child, not the leading `icon` prop. */}
            <Button onClick={confirm} color="primary" size="big">
              Yes, this is my store
              <ConfirmStoreChevron>
                <ChevronRight size={16} />
              </ConfirmStoreChevron>
            </Button>
            <ConfirmStoreSecondaryButton
              onClick={dismiss}
              color="secondary"
              size="big"
            >
              Choose a different store
            </ConfirmStoreSecondaryButton>
          </ConfirmStoreButtons>
          <ConfirmStoreFootnote as="p">
            Distance is approximate and does not indicate delivery availability.
          </ConfirmStoreFootnote>
        </ConfirmStoreView>
      </ModalContent>
    </ModalView>
  )
}

export default ConfirmStore
